import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';
import { ApplicationConfigService } from '../config/application-config.service';
import { createRequestOption } from '../request/request-util';
import { isPresent } from '../util/operators';
import { DATE_FORMAT } from '../util/input.constants';
import { getNotaFiscalIdentifier, ICarCompra, INotaFiscal } from '../models/nota-fiscal.model';

export type EntityResponseType = HttpResponse<INotaFiscal>;
export type EntityArrayResponseType = HttpResponse<INotaFiscal[]>;

@Injectable({ providedIn: 'root' })
export class NotaFiscalService {
  carRenovaSignal = signal([]);
  carAguardandoSignal = signal([]);
  protected resourceUrl = this.applicationConfigService.getEndpointFor('api/nota-fiscals');

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) {}

  create(notaFiscal: INotaFiscal): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(notaFiscal);
    return this.http
      .post<INotaFiscal>(this.resourceUrl, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(notaFiscal: INotaFiscal): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(notaFiscal);
    return this.http
      .put<INotaFiscal>(`${this.resourceUrl}/${getNotaFiscalIdentifier(notaFiscal) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(notaFiscal: INotaFiscal): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(notaFiscal);
    return this.http
      .patch<INotaFiscal>(`${this.resourceUrl}/${getNotaFiscalIdentifier(notaFiscal) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<INotaFiscal>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  getAprovadas(): Observable<any>{
    return this.http
      .get<any>(`${this.resourceUrl}/results-perspective`, { observe: 'response' })
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<INotaFiscal[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  findByUser(id: number): Observable<EntityArrayResponseType> {
    return this.http
      .get<INotaFiscal[]>(`${this.resourceUrl}/by-user/${id}`, { observe: 'response' });
  }

  getAllNotaFiscalsByCodigoCar(carCode: string): Observable<EntityArrayResponseType>{
    return this.http
    .get<INotaFiscal[]>(`${this.resourceUrl}/by-codigo-car`, {params:{carCode}, observe: 'response' });
  }

  loadAllPaginated(res?: any): Observable<any>{
    return this.http
      .get<any>(`${this.resourceUrl}/pagination`, { params: res, observe: 'response' })
      .pipe(map((res: any) => this.convertPageFromServer(res)));
  }

  notification(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<INotaFiscal[]>(`${this.resourceUrl}/notification`, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  listByUser(): Observable<EntityArrayResponseType> {
    return this.http
      .get<INotaFiscal[]>(`${this.resourceUrl}/list`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  addNotaFiscalToCollectionIfMissing(
    notaFiscalCollection: INotaFiscal[],
    ...notaFiscalsToCheck: (INotaFiscal | null | undefined)[]
  ): INotaFiscal[] {
    const notaFiscals: INotaFiscal[] = notaFiscalsToCheck.filter(isPresent);
    if (notaFiscals.length > 0) {
      const notaFiscalCollectionIdentifiers = notaFiscalCollection.map(notaFiscalItem => getNotaFiscalIdentifier(notaFiscalItem)!);
      const notaFiscalsToAdd = notaFiscals.filter(notaFiscalItem => {
        const notaFiscalIdentifier = getNotaFiscalIdentifier(notaFiscalItem);
        if (notaFiscalIdentifier == null || notaFiscalCollectionIdentifiers.includes(notaFiscalIdentifier)) {
          return false;
        }
        notaFiscalCollectionIdentifiers.push(notaFiscalIdentifier);
        return true;
      });
      return [...notaFiscalsToAdd, ...notaFiscalCollection];
    }
    return notaFiscalCollection;
  }

  protected convertDateFromClient(notaFiscal: INotaFiscal): INotaFiscal {
    return Object.assign({}, notaFiscal, {
      timestamp: dayjs(notaFiscal.timestamp)?.isValid() ? dayjs(notaFiscal.timestamp).format(DATE_FORMAT) : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.timestamp = dayjs(res.body.timestamp) ? dayjs(res.body.timestamp) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((notaFiscal: INotaFiscal) => {
        notaFiscal.timestamp = dayjs(notaFiscal.timestamp) ? dayjs(notaFiscal.timestamp) : undefined;
      });
    }
    return res;
  }

  protected convertPageFromServer(res: any): any {
    if (res.body) {
      res.body.content.forEach((notaFiscal: INotaFiscal) => {
        notaFiscal.timestamp = notaFiscal.timestamp ? dayjs(notaFiscal.timestamp) : undefined;
      });
    }
    return res;
  }


  upload(file: File): Observable<EntityResponseType> {
    const formData: FormData = new FormData();

    formData.append('file', file);
    return this.http.post<INotaFiscal>(this.resourceUrl, formData, { reportProgress: true, observe: 'response' });
  }

  renovar(id: number, dataRenovacao: Date): Observable<EntityResponseType> {
    const body = {
      dataRenovacao
    };
    return this.http
      .post<INotaFiscal>(`${this.resourceUrl}/renovarDocumento/${id}`, body, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  findTotalGerado(): Observable<HttpResponse<number>> {
    return this.http
    .get<number>(`${this.resourceUrl}/total-gerado`, { observe: 'response' });
  }

  findNotaFiscalPorCompra(res?: any): Observable<HttpResponse<ICarCompra[]>>{
    return this.http
      .get<ICarCompra[]>(`${this.resourceUrl}/compra`, { params: res, observe: 'response' })
      .pipe(map((res: HttpResponse<ICarCompra[]>) => this.convertNfDateArrayFromServer(res)));
  }

  protected convertNfDateArrayFromServer(res: HttpResponse<ICarCompra[]>): HttpResponse<ICarCompra[]> {
    if (res.body) {
      res.body.forEach((notaFiscal: ICarCompra) => {
        notaFiscal.nfDate = dayjs(notaFiscal.nfDate) ? dayjs(notaFiscal.nfDate) : undefined;
      });
    }
    return res;
  }
}
